import { useState, useEffect, useCallback } from "react";
import { TestType, TestTypeInterface } from "../../../DirectBilling/Edit/types/TestTypes.interface";
import { PaperEditBillInfoProps } from "../types/PaperEditBillInfo.types";
import { useAutoSaveNotes } from "../../../DirectBilling/Edit/hooks/useAutoSaveNotes";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { useBatchPrintPdf, BillPdfBatchResult } from "../../../../../../hooks/useBatchPrintPdf";
import { BillPdfBatchItemDtoPdfTypeEnum } from "../../../../../../api-client";

// Import modular hooks
import { useMenuActions } from "./useMenuActions";
import { useDialogStates } from "./useDialogStates";
import { useBillChangeTracking } from "./useBillChangeTracking";
import { useRxDeletion } from "./useRxDeletion";
import { useBillingOperations } from "./useBillingOperations";

/**
 * Refactored and modular custom hook that encapsulates the main logic of EditBillInfo.
 * Now organized with single responsibility principle and better separation of concerns.
 */
export const usePaperBillEditInfo = ({
  bills,
  setBills,
  onRowClick,
  onRxDelete,
  handleFullScreenDialogClose,
  onRemoveAll,
}: PaperEditBillInfoProps) => {
  // =====================================
  // STATE MANAGEMENT
  // =====================================

  // Menu and selection states
  const menuActions = useMenuActions();

  // Dialog states
  const dialogStates = useDialogStates();

  // Bill change tracking
  const changeTracking = useBillChangeTracking(bills);

  // Business logic hooks
  const rxDeletion = useRxDeletion();
  const billingOps = useBillingOperations();

  // Auto-save notes management
  const autoSaveNotes = useAutoSaveNotes({
    bills,
  });

  // Batch print hook - uses FlowProducer for parallel PDF generation
  const {
    isLoading: isBatchPrinting,
    progress: printProgress,
    startBatchPrint,
  } = useBatchPrintPdf(BillPdfBatchItemDtoPdfTypeEnum.PaperBill);

  // Set up auto-save handler after billingOps is available (similar to DirectBilling)
  useEffect(() => {
    autoSaveNotes.setAutoSaveHandler(
      async (billId: string, noteValue: string, updatedBill: any) => {
        try {
          // Create a bill with the updated note value to ensure we use the latest note
          const billWithUpdatedNote = {
            ...updatedBill,
            notes: noteValue,
          };

          // Save to backend using the billing operations
          const dto = billingOps.createBillingDataDto(billWithUpdatedNote, {
            [billId]: noteValue,
          });

          // Call the submitEditedBillingData directly
          await billingOps.submitEditedBillingData(billId, dto);
        } catch (error) {
          console.error("Auto-save error:", error);
          notificationStore.showMessage("Fehler beim automatischen Speichern der Notiz", "error");
          throw error; // Re-throw so the auto-save hook can handle it
        }
      },
    );
  }, [billingOps, autoSaveNotes]);

  // =====================================
  // REMAINING LOCAL STATE
  // =====================================

  // RX-related states
  const [rxNumber, setRxNumberInput] = useState("");
  const [rxNumberError, setRxNumberError] = useState("");
  const [selectedRxId, setSelectedRxId] = useState<string | null>(null);

  // Test and export states
  const [testToggleChecked, setTestToggleChecked] = useState(false);
  const [testTypeData, setTestTypeData] = useState<TestTypeInterface>({
    type: TestType.GLOBAL,
    email: undefined,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  // Utility states
  const today = new Date();
  const todayString = today.toDateString();

  // =====================================
  // BUSINESS LOGIC HANDLERS
  // =====================================

  /**
   * Handle RX deletion with improved logic
   */
  const handleDeleteClick = () => {
    dialogStates.setOpenDeleteDialog(true);
    menuActions.handleMenuClose();
  };

  const handleDeleteCancel = () => {
    dialogStates.setOpenDeleteDialog(false);
    menuActions.clearSelection();
  };

  const handleDeleteConfirm = async () => {
    if (!menuActions.selectedRx) return;

    await rxDeletion.handleRxDeletion(
      menuActions.selectedRx,
      bills,
      setBills,
      autoSaveNotes.notes,
      onRxDelete,
    );

    dialogStates.setOpenDeleteDialog(false);
    menuActions.clearSelection();
  };

  /**
   * Handle print operation (legacy sequential)
   */
  const handleDrucken = async (testType: TestType) => {
    setIsPrinting(true);

    await billingOps.handlePrint(bills, autoSaveNotes.notes, setBills, () => {
      changeTracking.updateSnapshot(bills);
      setIsPrinting(false);
    });
  };

  /**
   * Handle batch print operation with auto-save.
   * 1. Auto-save all bills first (to persist any pending changes)
   * 2. Start batch print workflow
   * 3. Update bills state with document info (user clicks to download)
   */
  const handleDruckenBatch = useCallback(async (): Promise<BillPdfBatchResult | null> => {
    try {
      // Step 1: Auto-save all bills first to persist any pending changes
      const savePromises = bills.map((bill) => {
        const dto = billingOps.createBillingDataDto(bill, autoSaveNotes.notes);
        return billingOps.submitEditedBillingData(bill.billId, dto);
      });

      await Promise.all(savePromises);

      // Step 2: Start batch print workflow
      const billIds = bills.map((b) => b.billId);
      const result = await startBatchPrint(billIds);

      if (result) {
        if (result.failureCount > 0) {
          notificationStore.showMessage(
            `${result.successCount}/${result.totalCount} PDFs erfolgreich erstellt. ${result.failureCount} Fehler.`,
            "warning",
          );
        } else {
          notificationStore.showMessage(
            `Alle ${result.totalCount} PDFs erfolgreich erstellt.`,
            "success",
          );
        }

        // Step 3: Update bills state with generated document info
        // User will click to download (Paper Bill uses click-to-download pattern)
        const successfulResults = result.results.filter((r) => r.success && r.documentId);
        if (successfulResults.length > 0) {
          setBills((prevBills: any[]) =>
            prevBills.map((bill: any) => {
              const matchingResult = successfulResults.find((r) => r.entityId === bill.billId);
              if (matchingResult && matchingResult.documentId) {
                return {
                  ...bill,
                  docDto: {
                    id: matchingResult.documentId,
                    accessUrl: matchingResult.blobUrl,
                  },
                };
              }
              return bill;
            }),
          );
        }

        // Update snapshot after successful print
        changeTracking.updateSnapshot(bills);
      }

      return result;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Unbekannter Fehler";
      notificationStore.showMessage(
        `Beim Drucken der Rechnung ist ein Problem aufgetreten: ${errorMessage}`,
        "error",
      );
      return null;
    }
  }, [bills, autoSaveNotes.notes, billingOps, startBatchPrint, changeTracking, setBills]);

  /**
   * Handle export operation
   */
  const handleExport = () => {
    dialogStates.setOpenExportDialog(true);
  };

  const handleExportConfirm = async () => {
    try {
      dialogStates.setOpenExportDialog(false);
      setIsExporting(true);

      await billingOps.handleExport(bills, () => {
        setBills([]);
        handleFullScreenDialogClose(true);
      });
    } finally {
      setIsExporting(false);
    }
  };

  // =====================================
  // RETURN INTERFACE (organized and clean)
  // =====================================

  return {
    // ============= STATES =============
    // Menu and selection
    anchorEl: menuActions.anchorEl,
    selectedRx: menuActions.selectedRx,

    // Bills data
    bills,

    // Dialog states
    openDeleteDialog: dialogStates.openDeleteDialog,
    openExportDialog: dialogStates.openExportDialog,
    openSingleRxDialog: dialogStates.openSingleRxDialog,
    openTestTypeDialog: dialogStates.openTestTypeDialog,

    // RX states
    rxNumberInput: rxNumber,
    rxNumberError,
    selectedRxId,

    // Auto-save notes
    notes: autoSaveNotes.notes,
    updateNote: autoSaveNotes.updateNote,
    isSaving: autoSaveNotes.isSaving,
    lastSavedAt: autoSaveNotes.lastSavedAt,

    // Change tracking
    billsChanged: changeTracking.billsChanged,
    isSaved: changeTracking.isSaved,

    // Test and export states
    testToggleChecked,
    testTypeData,
    isExporting,
    isPrinting,
    isBatchPrinting,
    printProgress,
    todayString,

    // ============= SETTERS =============
    setSelectedRx: menuActions.setSelectedRx,
    setOpenDeleteDialog: dialogStates.setOpenDeleteDialog,
    setOpenExportDialog: dialogStates.setOpenExportDialog,
    setOpenSingleRxDialog: dialogStates.setOpenSingleRxDialog,
    setOpenTestTypeDialog: dialogStates.setOpenTestTypeDialog,
    setRxNumberInput,
    setRxNumberError,
    setSelectedRxId,
    setBillsChanged: changeTracking.setBillsChanged,
    setTestToggleChecked,
    setTestTypeData,

    // ============= HANDLERS =============
    // Menu actions
    handleMenuOpen: menuActions.handleMenuOpen,
    handleMenuClose: menuActions.handleMenuClose,

    // Delete operations
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,

    // Business operations
    handleDrucken,
    handleDruckenBatch,
    handleExport,
    handleExportConfirm,

    // Props passthrough
    onRowClick,
    onRemoveAll,
  };
};
